import * as React from "react";

import {
  DataMutationAddToWishlist_Request,
  useDataMutationAddToWishlist,
  useJoinDrawer,
  useJoinDrawerUserExperience,
  useLoginForm,
} from "../../../Common";

import { usePdpAddToWishlist } from "./pdp-add-to-wishlist-provider";

interface PdpWishlistUserExperienceConsumerInterface {
  isLoading?: boolean;

  presentingUserExperienceStep: WishlistUserExperience_Step;

  handlePdpAddToWishlistButtonClick_NotLoggedInUser: () => Promise<void>;
  handlePdpAddToWishlistButtonClick_AlreadyLoggedInUser: () => Promise<void>;

  handleCloseOfTheRightDrawerAtAnyTime: () => void;

  handleLoginSuccess: () => void;
}
const defaultValue =
  {} as unknown as PdpWishlistUserExperienceConsumerInterface;
const PdpWishtlistUserExperienceContext = React.createContext(defaultValue);

export enum WishlistUserExperience_Step {
  NOT_PRESENTING = "NOT_PRESENTING",
  CLOSING = "CLOSING",
  NEED_TO_LOGIN = "NEED_TO_LOGIN",
  ADDED_TO_WISHLIST_SUCCESS_SCREEN = "ADDED_TO_WISHLIST_SUCCESS_SCREEN",
}

export const PdpWishlistUserExperienceProvider: React.FunctionComponent = (
  props
) => {
  const { isLoggedIn } = useLoginForm();

  const { handleClose: joinDrawer_handleClose } = useJoinDrawer();

  const { handleBeginTheJoinDrawerUserExperience } =
    useJoinDrawerUserExperience();

  const { readWishlistItemsToAddFromTheCurrentStateOfTheDocument } =
    usePdpAddToWishlist();

  const { mutateAsync: dataMutation_addToWishlist } =
    useDataMutationAddToWishlist();

  const [presentingUserExperienceStep, setPresentingUserExperienceStep] =
    React.useState<WishlistUserExperience_Step>(
      WishlistUserExperience_Step.NOT_PRESENTING
    );

  const [isLoading, setIsLoading] = React.useState(false);

  const handleCloseOfTheRightDrawerAtAnyTime: PdpWishlistUserExperienceConsumerInterface["handleCloseOfTheRightDrawerAtAnyTime"] =
    React.useCallback(() => {
      if (
        presentingUserExperienceStep ===
        WishlistUserExperience_Step.ADDED_TO_WISHLIST_SUCCESS_SCREEN
      ) {
        setPresentingUserExperienceStep(WishlistUserExperience_Step.CLOSING);
      } else {
        setPresentingUserExperienceStep(
          WishlistUserExperience_Step.NOT_PRESENTING
        );
      }
    }, [presentingUserExperienceStep, setPresentingUserExperienceStep]);

  const addTheItemsToTheWishlistAndShowTheSuccessScreen =
    React.useCallback(async () => {
      setIsLoading(true);

      try {
        const addTheseItemsToWishlist =
          await readWishlistItemsToAddFromTheCurrentStateOfTheDocument();

        const addToWishlistRequest: DataMutationAddToWishlist_Request = {
          addTheseItemsToWishlist,
        };

        const addToWishlistResponse = await dataMutation_addToWishlist(
          addToWishlistRequest
        );

        const areThereItems =
          addToWishlistResponse.wishlist &&
          addToWishlistResponse.wishlist.items &&
          (addToWishlistResponse.wishlist.items?.length || 0) > 0;
        if (!areThereItems) {
          throw new Error("No items were added to the wishlist");
        }

        setIsLoading(false);

        setPresentingUserExperienceStep(
          WishlistUserExperience_Step.ADDED_TO_WISHLIST_SUCCESS_SCREEN
        );

        const wishlistHeaderBubbleUpdated = await new Promise((resolve) => {
          (window.require as any)(["jquery"], function ($: any) {
            const $wishlistHeaderContainer = $(".wishlist-header-container");
            const $counterQty = $wishlistHeaderContainer.find(".counter.qty");
            const $counterValue = $counterQty.find(".value");
            $counterValue.html(
              `${addToWishlistResponse.wishlist.items?.length || 0}`
            );
            if (addToWishlistResponse.wishlist.items?.length || 0 > 0) {
              $counterQty.removeClass("empty");
            } else {
              $counterQty.addClass("empty");
            }
            resolve(true);
          });
        });
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        handleCloseOfTheRightDrawerAtAnyTime();
      }
    }, [
      setIsLoading,
      readWishlistItemsToAddFromTheCurrentStateOfTheDocument,
      dataMutation_addToWishlist,
      setPresentingUserExperienceStep,
      handleCloseOfTheRightDrawerAtAnyTime,
    ]);

  const handlePdpAddToWishlistButtonClick_AlreadyLoggedInUser: PdpWishlistUserExperienceConsumerInterface["handlePdpAddToWishlistButtonClick_AlreadyLoggedInUser"] =
    React.useCallback(async () => {
      await addTheItemsToTheWishlistAndShowTheSuccessScreen();
    }, [addTheItemsToTheWishlistAndShowTheSuccessScreen]);

  const handlePdpAddToWishlistButtonClick_NotLoggedInUser: PdpWishlistUserExperienceConsumerInterface["handlePdpAddToWishlistButtonClick_NotLoggedInUser"] =
    React.useCallback(async () => {
      if (isLoggedIn) {
        return handlePdpAddToWishlistButtonClick_AlreadyLoggedInUser();
      } else {
        setPresentingUserExperienceStep(
          WishlistUserExperience_Step.NEED_TO_LOGIN
        );
        handleBeginTheJoinDrawerUserExperience();
      }
    }, [
      handleBeginTheJoinDrawerUserExperience,
      setPresentingUserExperienceStep,
      handlePdpAddToWishlistButtonClick_AlreadyLoggedInUser,
      isLoggedIn,
    ]);

  const handleLoginSuccess: PdpWishlistUserExperienceConsumerInterface["handleLoginSuccess"] =
    React.useCallback(async () => {
      joinDrawer_handleClose();

      await addTheItemsToTheWishlistAndShowTheSuccessScreen();
    }, [
      joinDrawer_handleClose,
      addTheItemsToTheWishlistAndShowTheSuccessScreen,
    ]);

  const value: PdpWishlistUserExperienceConsumerInterface = React.useMemo(
    () => ({
      isLoading,
      presentingUserExperienceStep,
      handlePdpAddToWishlistButtonClick_NotLoggedInUser,
      handlePdpAddToWishlistButtonClick_AlreadyLoggedInUser,
      handleCloseOfTheRightDrawerAtAnyTime,
      handleLoginSuccess,
    }),
    [
      isLoading,
      presentingUserExperienceStep,
      handlePdpAddToWishlistButtonClick_NotLoggedInUser,
      handlePdpAddToWishlistButtonClick_AlreadyLoggedInUser,
      handleCloseOfTheRightDrawerAtAnyTime,
      handleLoginSuccess,
    ]
  );
  return (
    <PdpWishtlistUserExperienceContext.Provider value={value}>
      {props.children || []}
    </PdpWishtlistUserExperienceContext.Provider>
  );
};

export const usePdpWishlistUserExperience = () => {
  const context = React.useContext(PdpWishtlistUserExperienceContext);
  if (context === defaultValue) {
    throw new Error(
      "usePdpWishlistUserExperience must be used within a PdpWishlistUserExperienceProvider"
    );
  }
  return context;
};
